import tzlookup from 'tz-lookup';

const UTC_FALLBACK = 'UTC';

export function resolveTimezoneFromCoords(lat, lng) {
  if (lat == null || lng == null) return null;
  try {
    return tzlookup(Number(lat), Number(lng));
  } catch {
    return null;
  }
}

export function getCourseTimezone(round) {
  if (!round) return UTC_FALLBACK;
  const tz = round.timezone ?? round.round_timezone;
  if (tz) return tz;
  const lat = round.course_latitude ?? round.courseLatitude;
  const lng = round.course_longitude ?? round.courseLongitude;
  if (lat != null && lng != null) {
    try {
      return tzlookup(Number(lat), Number(lng));
    } catch {
      /* fall through */
    }
  }
  if (typeof console !== 'undefined' && console.warn) {
    console.warn('[timeHelpers] no timezone and no usable lat/lng on round; falling back to UTC', {
      id: round.id,
    });
  }
  return UTC_FALLBACK;
}

// Module-level override set by AuthContext / Settings when the user's
// `user_profiles.timezone` is known. Lets the helper prefer the user's
// explicit setting over the browser's resolved zone (which is sometimes
// wrong due to OS locale settings, Apple Private Relay, VPNs, etc.).
let viewerTimezoneOverride = null;

export function setViewerTimezoneOverride(tz) {
  viewerTimezoneOverride = tz || null;
}

export function getViewerTimezone() {
  if (viewerTimezoneOverride) return viewerTimezoneOverride;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || UTC_FALLBACK;
  } catch {
    return UTC_FALLBACK;
  }
}

function tzOffsetMillisAt(tz, utcInstantMs) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const map = {};
  for (const part of dtf.formatToParts(new Date(utcInstantMs))) {
    map[part.type] = part.value;
  }
  const hour = map.hour === '24' ? 0 : Number(map.hour);
  const asIfUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    hour,
    Number(map.minute),
    Number(map.second),
  );
  return asIfUTC - utcInstantMs;
}

function parseDateTriple(date) {
  if (!date) return null;
  const m = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function parseTimeParts(time) {
  if (!time) return null;
  const parts = String(time).split(':').map(Number);
  if (Number.isNaN(parts[0])) return null;
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

function pickDate(round) {
  return round?.date ?? round?.round_date ?? null;
}

function pickTime(round) {
  return round?.time ?? round?.round_time ?? null;
}

export function roundStartInstant(round) {
  if (!round) return null;
  const dateTriple = parseDateTriple(pickDate(round));
  const timeParts = parseTimeParts(pickTime(round));
  if (!dateTriple || !timeParts) return null;
  const tz = getCourseTimezone(round);
  const [y, mo, d] = dateTriple;
  const [h, mi, s] = timeParts;
  const wallAsUTC = Date.UTC(y, mo - 1, d, h, mi, s);
  const offset1 = tzOffsetMillisAt(tz, wallAsUTC);
  const candidate = wallAsUTC - offset1;
  const offset2 = tzOffsetMillisAt(tz, candidate);
  if (offset1 === offset2) return new Date(candidate);
  return new Date(wallAsUTC - offset2);
}

const DATE_FORMAT_DEFAULTS = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

export function formatRoundDate(round, options = {}) {
  const dateTriple = parseDateTriple(pickDate(round));
  if (!dateTriple) return '';
  const [y, mo, d] = dateTriple;
  const tz = getCourseTimezone(round);
  const noonAtCourse = roundStartInstant({ ...round, date: pickDate(round), time: '12:00' });
  const referenceInstant = noonAtCourse || new Date(Date.UTC(y, mo - 1, d, 12));
  const { locale = 'en-GB', ...formatOpts } = options;
  return new Intl.DateTimeFormat(locale, {
    ...DATE_FORMAT_DEFAULTS,
    ...formatOpts,
    timeZone: tz,
  }).format(referenceInstant);
}

// CLDR short tz names live in locale-specific data. en-GB has "BST"/"GMT"
// but not "EDT"/"EST"; en-US is the opposite. We try locales in order and
// prefer a real abbreviation over a "GMT+/-N" offset string.
const ZONE_LABEL_LOCALES = ['en-GB', 'en-US'];

export function getZoneShortLabel(tz, instant) {
  if (!tz) return '';
  const ref = instant instanceof Date ? instant : new Date();
  let fallback = '';
  for (const locale of ZONE_LABEL_LOCALES) {
    try {
      const parts = new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        timeZoneName: 'short',
        hour: '2-digit',
        hour12: false,
      }).formatToParts(ref);
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      if (!tzPart) continue;
      // "GMT", "GMT+1", "GMT-4" etc. are offset placeholders the locale
      // emits when it has no abbreviation for the zone. Hold onto them as
      // a last-resort fallback while we try the remaining locales.
      if (/^GMT([+-]\d|$)/.test(tzPart.value)) {
        if (!fallback) fallback = tzPart.value;
        continue;
      }
      return tzPart.value;
    } catch {
      /* try next locale */
    }
  }
  return fallback;
}

function formatTimeInZone(instant, tz, { locale = 'en-GB', hour12 = false } = {}) {
  if (!instant) return '';
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    hour: hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12,
  }).format(instant);
}

export function formatRoundTime(round, options = {}) {
  const { includeZoneLabel = true, locale = 'en-GB', hour12 = false } = options;
  const tz = getCourseTimezone(round);
  const instant = roundStartInstant(round);
  if (!instant) {
    const fallback = parseTimeParts(pickTime(round));
    if (!fallback) return '';
    const [h, m] = fallback;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const wall = formatTimeInZone(instant, tz, { locale, hour12 });
  if (!includeZoneLabel) return wall;
  const label = getZoneShortLabel(tz, instant);
  return label ? `${wall} ${label}` : wall;
}

function localDateKey(instant, tz) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant);
  } catch {
    return null;
  }
}

export function formatRoundTimeWithViewerNote(round, options = {}) {
  const { viewerTz: viewerTzOverride, locale = 'en-GB', hour12 = false } = options;
  const tz = getCourseTimezone(round);
  const viewerTz = viewerTzOverride || getViewerTimezone();
  const instant = roundStartInstant(round);
  const primary = formatRoundTime(round, { includeZoneLabel: true, locale, hour12 });
  if (!instant || viewerTz === tz) {
    return { primary, secondary: null };
  }
  const viewerWall = formatTimeInZone(instant, viewerTz, { locale, hour12 });
  const viewerLabel = getZoneShortLabel(viewerTz, instant);

  // Two different IANA zones can share the same offset and short label at a
  // given moment (e.g. America/New_York and America/Toronto are both EDT in
  // summer; Europe/London and Atlantic/Reykjavik are both GMT in winter). If
  // the viewer's rendered output matches the course's, the secondary line
  // would be visually identical to the primary — suppress it.
  const courseWall = formatTimeInZone(instant, tz, { locale, hour12 });
  const courseLabel = getZoneShortLabel(tz, instant);
  if (viewerWall === courseWall && viewerLabel === courseLabel) {
    return { primary, secondary: null };
  }

  // If the viewer's local date differs from the course's local date (e.g. a
  // UK Sunday 04:00 BST round is Saturday 20:00 PDT for an LA viewer),
  // prepend the viewer's short weekday to avoid the user assuming the
  // viewer-local time is on the same calendar day as the round.
  const courseDateKey = localDateKey(instant, tz);
  const viewerDateKey = localDateKey(instant, viewerTz);
  let dayPrefix = '';
  if (courseDateKey && viewerDateKey && courseDateKey !== viewerDateKey) {
    try {
      dayPrefix = new Intl.DateTimeFormat(locale, {
        timeZone: viewerTz,
        weekday: 'short',
      }).format(instant) + ' ';
    } catch { /* fall through */ }
  }

  const secondary = viewerLabel
    ? `${dayPrefix}${viewerWall} ${viewerLabel} your time`
    : `${dayPrefix}${viewerWall} your time`;
  return { primary, secondary };
}
